import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useApi from "../hooks/useApi";

const InterestModal = ({ show, onClose, courseId }) => {
    const { publicAuth } = useApi();

    // ------ form state (array form) ------
    const [formRows, setFormRows] = useState([{ name: "", phone: "", email: "", gender: "" }]);
    const row = formRows[0];

    const updateField = (field, value, idx = 0) =>
        setFormRows((rows) => {
            const next = [...rows];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });

    const [cities, setCities] = useState([]);
    const [selectedState, setSelectedState] = useState("");
    const [selectedCity, setSelectedCity] = useState(null); // full city object

    // ------ flow state ------
    const [step, setStep] = useState("register"); // "register" | "otp"
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // OTP
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [canResend, setCanResend] = useState(false);

    // Holds the payload we submitted (for re-sending OTP or verifying)
    const [submissionData, setSubmissionData] = useState(null);

    // ---------- effects ----------
    useEffect(() => {
        if (!show) return;

        // reset modal UI when opened
        resetForm();
        fetchCities();

        // prevent body scroll under modal
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = "15px";
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (resendTimer > 0) {
            const t = setTimeout(() => setResendTimer((x) => x - 1), 1000);
            return () => clearTimeout(t);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    const states = useMemo(() => {
        const set = new Set();
        cities.forEach((c) => {
            if (c?.state_name) set.add(c.state_name);
        });
        return Array.from(set).sort();
    }, [cities]);

    const filteredCities = useMemo(() => {
        if (!selectedState) return [];
        return cities.filter((c) => c.state_name === selectedState);
    }, [cities, selectedState]);

    // ---------- api ----------
    const fetchCities = async () => {
        try {
            const res = await publicAuth("cities", "GET");
            const list = res?.data?.data || res?.data?.cities || [];
            setCities(list);
        } catch (e) {
            console.error("cities fetch error:", e);
            setCities([]);
        }
    };

    // ---------- handlers ----------
    const resetForm = () => {
        setFormRows([{ name: "", phone: "", email: "", gender: "" }]);
        setSelectedState("");
        setSelectedCity(null);
        setErrors({});
        setIsLoading(false);
        setStep("register");

        // otp state
        setOtp("");
        setOtpError("");
        setResendTimer(0);
        setCanResend(false);
        setSubmissionData(null);
    };

    const validateForm = () => {
        const e = {};
        const { name, email, phone, gender } = row;

        if (!name.trim()) e.name = "Name is required";
        else if (!/^[a-zA-Z\s]+$/.test(name)) e.name = "Only letters and spaces allowed";

        if (!email.trim()) e.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Invalid email";

        if (!phone.trim()) e.phone = "Phone is required";
        else if (!/^[6-9]\d{9}$/.test(phone)) e.phone = "Enter valid 10-digit Indian number";

        if (!gender) e.gender = "Please select gender";
        if (!selectedState) e.state = "Please select state";
        if (!selectedCity) e.city = "Please select city";

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // === API flow ===
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const payload = {
                ...row, // name, phone, email, gender
                city_id: selectedCity.id,
                course_id: courseId,
                // If you want to keep an explicit array of values too:
                values: [row],
            };

            const res = await publicAuth(`register-interest`, "POST", payload);

            if (res.data.status) {
                // Save submission data for OTP verification
                setSubmissionData(payload);

                // Start OTP flow
                setResendTimer(30);
                setCanResend(false);
                setStep("otp");
            } else {
                Swal.fire("Error", res.data.message || "Submission failed", "error");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || "Network error while submitting.";
            Swal.fire("Error", msg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const verifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            setOtpError("Please enter a valid 6-digit OTP");
            return false;
        }

        if (!submissionData?.email) {
            setOtpError("Missing submission context. Please submit again.");
            setStep("register");
            return false;
        }

        setIsLoading(true);
        try {
            const res = await publicAuth(`verify-interest-otp`, "POST", {
                email: submissionData.email,
                otp: otp,
            });

            if (res.data.status) {
                await Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text:
                        "Registration successful! Team member will get in touch with you within 3 Working days. " +
                        "Please create a login ID & explore Free Mock up Questions besides our valuable offerings. " +
                        "Feel free to checkout other amazing offers exclusively crafted for you.",
                    confirmButtonText: "OK",
                });

                resetForm();
                onClose && onClose();
                return true;
            } else {
                setOtpError(res.data.message || "Invalid OTP");
                return false;
            }
        } catch (err) {
            setOtpError(err?.response?.data?.message || "OTP verification failed");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const resendOtp = async () => {
        if (!submissionData) return;

        setIsLoading(true);
        try {
            await publicAuth("register-interest", "POST", submissionData); // re-trigger sends OTP again
            setResendTimer(30);
            setCanResend(false);
            setOtp("");
            setOtpError("");
            Swal.fire({
                icon: "success",
                title: "OTP Resent!",
                text: "A new OTP has been sent to your email.",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || "Failed to resend OTP", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // ---------- render helpers ----------
    const onCityChange = (e) => {
        const val = e.target.value;
        const cityObj = filteredCities.find((c) => String(c.id) === String(val)) || null;
        setSelectedCity(cityObj);
        setErrors((prev) => ({ ...prev, city: "" }));
    };

    if (!show) return null;

    // ---------- UI ----------
    return (
        <>
            <div className="modal-overlay">
                <div className="modal-box" style={{ maxWidth: "980px" }}>
                    <button
                        onClick={() => {
                            resetForm();
                            onClose && onClose();
                        }}
                        className="registerclose close border-0"
                        aria-label="Close modal"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>

                    {/* Split layout: left image / right content */}
                    <div className="modal-content p-0" style={{ overflow: "hidden" }}>
                        <div className="row g-0">
                            {/* LEFT IMAGE */}
                            <div className="col-md-5 d-none d-md-block">
                                <div
                                    style={{
                                        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner/student-driver.jpg)`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        minHeight: 520,
                                    }}
                                />
                            </div>

                            {/* RIGHT CONTENT */}
                            <div className="col-12 col-md-7">
                                <div className="p-4 p-md-5">
                                    <h2 className="mb-2" style={{ color: "#343a40" }}>
                                        {step === "otp" ? "Verify OTP" : "Register Your Interest"}
                                    </h2>
                                    <p className="text-muted mb-4">
                                        {step === "otp"
                                            ? `Enter the 6-digit OTP sent to ${submissionData?.email || row.email}`
                                            : "Share your details and we'll reach out soon."}
                                    </p>

                                    {/* STEP: REGISTER */}
                                    {step === "register" && (
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleSubmit();
                                            }}
                                        >
                                            <div className="form-group mb-3">
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                                    placeholder="Full Name*"
                                                    value={row.name}
                                                    onChange={(e) => {
                                                        updateField("name", e.target.value);
                                                        setErrors((p) => ({ ...p, name: "" }));
                                                    }}
                                                    disabled={isLoading}
                                                />
                                                {errors.name && <div className="text-danger">{errors.name}</div>}
                                            </div>

                                            <div className="form-group mb-3">
                                                <div className="input-group">
                                                    <div className="input-group-prepend">
                                                        <span className="input-group-text">+91</span>
                                                    </div>
                                                    <input
                                                        type="tel"
                                                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                                        placeholder="Mobile Number*"
                                                        value={row.phone}
                                                        onChange={(e) => {
                                                            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                                                            updateField("phone", v);
                                                            setErrors((p) => ({ ...p, phone: "" }));
                                                        }}
                                                        maxLength={10}
                                                        disabled={isLoading}
                                                    />
                                                </div>
                                                {errors.phone && <div className="text-danger">{errors.phone}</div>}
                                            </div>

                                            <div className="form-group mb-3">
                                                <input
                                                    type="email"
                                                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                                    placeholder="Email*"
                                                    value={row.email}
                                                    onChange={(e) => {
                                                        updateField("email", e.target.value);
                                                        setErrors((p) => ({ ...p, email: "" }));
                                                    }}
                                                    disabled={isLoading}
                                                />
                                                {errors.email && <div className="text-danger">{errors.email}</div>}
                                            </div>

                                            {/* Gender radio */}
                                            <div className="form-group mb-3 d-flex">
                                                <label className="form-label d-block me-3">Gender*</label>
                                                <div className="d-flex flex-wrap gap-4">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="gender"
                                                            id="gender-male"
                                                            value="male"
                                                            checked={row.gender === "male"}
                                                            onChange={(e) => {
                                                                updateField("gender", e.target.value);
                                                                setErrors((p) => ({ ...p, gender: "" }));
                                                            }}
                                                            disabled={isLoading}
                                                        />
                                                        <label className="form-check-label" htmlFor="gender-male">
                                                            Male
                                                        </label>
                                                    </div>

                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="gender"
                                                            id="gender-female"
                                                            value="female"
                                                            checked={row.gender === "female"}
                                                            onChange={(e) => {
                                                                updateField("gender", e.target.value);
                                                                setErrors((p) => ({ ...p, gender: "" }));
                                                            }}
                                                            disabled={isLoading}
                                                        />
                                                        <label className="form-check-label" htmlFor="gender-female">
                                                            Female
                                                        </label>
                                                    </div>
                                                </div>
                                                {errors.gender && <div className="text-danger mt-1">{errors.gender}</div>}
                                            </div>

                                            <div className="form-group mb-3">
                                                <select
                                                    className={`form-control ${errors.state ? "is-invalid" : ""}`}
                                                    value={selectedState}
                                                    onChange={(e) => {
                                                        setSelectedState(e.target.value);
                                                        setSelectedCity(null);
                                                        setErrors((p) => ({ ...p, state: "" }));
                                                    }}
                                                    disabled={isLoading}
                                                >
                                                    <option value="">-- State* --</option>
                                                    {states.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.state && <div className="text-danger">{errors.state}</div>}
                                            </div>

                                            <div className="form-group mb-4">
                                                <select
                                                    className={`form-control ${errors.city ? "is-invalid" : ""}`}
                                                    value={selectedCity?.id || ""}
                                                    onChange={onCityChange}
                                                    disabled={isLoading || !selectedState}
                                                >
                                                    <option value="">-- City* --</option>
                                                    {filteredCities.map((c) => (
                                                        <option key={c.id} value={c.id}>
                                                            {c.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.city && <div className="text-danger">{errors.city}</div>}
                                            </div>

                                            <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" /> Submitting...
                                                    </>
                                                ) : (
                                                    "SUBMIT"
                                                )}
                                            </button>
                                        </form>
                                    )}

                                    {/* STEP: OTP */}
                                    {step === "otp" && (
                                        <div>
                                            <div className="form-group mb-3">
                                                <label className="form-label">Enter OTP</label>
                                                <input
                                                    type="text"
                                                    className={`form-control text-center ${otpError ? "is-invalid" : ""}`}
                                                    value={otp}
                                                    onChange={(e) => {
                                                        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                                                        setOtp(v);
                                                        setOtpError("");
                                                    }}
                                                    placeholder="------"
                                                    maxLength={6}
                                                    disabled={isLoading}
                                                />
                                                {otpError && <div className="text-danger text-center">{otpError}</div>}
                                            </div>

                                            <div className="text-center mb-3">
                                                <small>
                                                    Didn't receive?{" "}
                                                    <span
                                                        className={`resend-link ${!canResend || isLoading ? "disabled" : ""}`}
                                                        onClick={canResend && !isLoading ? resendOtp : undefined}
                                                        role="button"
                                                        style={{ textDecoration: canResend ? "underline" : "none" }}
                                                    >
                                                        Resend OTP {resendTimer > 0 ? `in ${resendTimer}s` : ""}
                                                    </span>
                                                </small>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    onClick={() => setStep("register")}
                                                    className="btn btn-outline-secondary flex-grow-1"
                                                    disabled={isLoading}
                                                >
                                                    Back
                                                </button>
                                                <button
                                                    onClick={verifyOtp}
                                                    className="btn btn-primary flex-grow-1"
                                                    disabled={isLoading || otp.length !== 6}
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" />
                                                            Verifying...
                                                        </>
                                                    ) : (
                                                        "VERIFY OTP"
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default InterestModal;
